import React, { useRef, useState, useEffect } from 'react';
import { Field, Formik } from 'formik';
import Select from 'react-select';
import { Country, State, City } from 'country-state-city';
import { usePincodeLookup } from '../../hooks/usePincodeLookup';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCheck,
  FaSave,
  FaSpinner,
  FaExclamationCircle,
} from 'react-icons/fa';

import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
import Wrapper from '../../components/Wrapper';
import { addFarmer, resetAddFarmerStatus } from '../../store/features/usersSlice';
import { fetchPartners } from '../../store/features/partnersSlice';
import { fetchFieldOfficersByAdminId } from '../../store/features/fieldOfficerByPartnerSlice';
import { getRole } from '../../utils/role';
import '../../styles/addFarmer.css';

/* VALIDATION */
const schema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  
  mobile: Yup.string()
    .required('Mobile is required')
    .test('is-valid-phone', 'Invalid Phone number', (value) => 
      value ? isValidPhoneNumber(value) : false
    ),
    
  email: Yup.string().email('Invalid email address'),
  address: Yup.string().required('Address is required'),
  pincode: Yup.string()
    .required('Zip/Postal Code is required')
    .matches(/^[a-zA-Z0-9\s\-]{3,12}$/, 'Invalid Zip/Postal Code'),
  country: Yup.string().required('Country is required'),
  state: Yup.string().required('State is required'),
  city: Yup.string().required('City is required'),
});

const AddFarmer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const stateSelectRef = useRef(null);
  const cityRef = useRef(null);
  const formApiRef = useRef({});
  
  const [countryCode, setCountryCode] = useState('');
  const [stateCode, setStateCode] = useState('');
  
  const { pincodeData, loading: pincodeLoading, lookupPincode, reset: resetPincode } =
    usePincodeLookup();

  const role = getRole();
  const { addFarmerStatus, error: addFarmerError } = useSelector((state) => state.users);
  const { partner } = useSelector((state) => state.partner);
  const { partners, status: partnersStatus } = useSelector((state) => state.partners);
  const { fieldOfficers, status: fieldOfficersStatus } = useSelector(
    (state) => state.fieldOfficerByPartner || {}
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const prevStatusRef = useRef(null);

  // Reset add-farmer status when leaving the page so next visit is clean
  useEffect(() => {
    return () => {
      dispatch(resetAddFarmerStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (addFarmerStatus === 'succeeded' && prevStatusRef.current !== 'succeeded') {
      setShowSuccess(true);
      dispatch(resetAddFarmerStatus());
      const timer = setTimeout(() => {
        navigate('/users');
      }, 2500);
      prevStatusRef.current = addFarmerStatus;
      return () => clearTimeout(timer);
    }
    prevStatusRef.current = addFarmerStatus;
  }, [addFarmerStatus, navigate, dispatch]);

  useEffect(() => {
    if (role === 'SUPPORT' && partnersStatus === 'idle') {
      dispatch(fetchPartners({ page: 1, limit: 100 }));
    }
  }, [dispatch, role, partnersStatus]);

  useEffect(() => {
    if ((role === 'PARTNER' || role === 'TEAM_MEMBER') && partner?.admin_id) {
      dispatch(fetchFieldOfficersByAdminId({ admin_id: partner.admin_id }));
    }
  }, [dispatch, role, partner?.admin_id]);

  const updatedCountries = () =>
    Country.getAllCountries().map((country) => ({
      label: country.name,
      value: country.isoCode,
      ...country,
    }));

  const updatedStates = (isoCountryCode) =>
    isoCountryCode ? State.getStatesOfCountry(isoCountryCode).map((state) => ({
      label: state.name,
      value: state.isoCode,
      ...state,
    })) : [];

  const updatedCities = (isoCountryCode, isoStateCode, pincodeDistrict) => {
    if (!isoCountryCode || !isoStateCode) return [];
    const list = City.getCitiesOfState(isoCountryCode, isoStateCode).map((c) => ({
      label: c.name,
      value: c.name,
    }));
    if (pincodeDistrict && !list.some((c) => c.value === pincodeDistrict)) {
      list.unshift({ label: pincodeDistrict, value: pincodeDistrict });
    }
    return list;
  };

  useEffect(() => {
    if (!pincodeData || !formApiRef.current.setFieldValue) return;
    const { setFieldValue } = formApiRef.current;
    
    setFieldValue('country', 'India');
    setCountryCode('IN');
    
    const inStates = updatedStates('IN');
    const matchedState = inStates.find((s) => s.label === pincodeData.state || s.value === pincodeData.state);
    
    if (matchedState) {
      setFieldValue('state', matchedState.label);
      setStateCode(matchedState.value);
    } else {
      setFieldValue('state', pincodeData.state);
    }
    
    setFieldValue('city', pincodeData.district);
    cityRef.current?.clearValue();
  }, [pincodeData]);

  const initialValues = {
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    address: '',
    pincode: '',
    country: '',
    state: '',
    city: '',
    adminId: (role === 'PARTNER' || role === 'TEAM_MEMBER') ? String(partner?.admin_id || '') : '',
    fieldOfficerId: '',
  };

  return (
    <Wrapper noScroll>
      <Formik
        initialValues={initialValues}
        validationSchema={schema.concat(
          Yup.object({
            ...(role === 'SUPPORT' && {
              adminId: Yup.string().required('Partner is required'),
            }),
            ...(((role === 'PARTNER' || role === 'TEAM_MEMBER') || role === 'SUPPORT') && {
              fieldOfficerId: Yup.string(),
            }),
          })
        )}
        enableReinitialize
        onSubmit={(values) => {
          const selectedPartner =
            role === 'SUPPORT'
              ? partners.find((p) => String(p.admin_id) === String(values.adminId))
              : partner;

          let formattedMobile = values.mobile;
          if (formattedMobile && formattedMobile.startsWith('+91')) {
            formattedMobile = formattedMobile.replace('+91', '');
          }

          const payload = {
            first_name: values.firstName,
            last_name: values.lastName,
            mobile_no: formattedMobile,
            email_id: values.email,
            address: values.address,
            pincode: values.pincode,
            city: values.city,
            state: values.state,
            ...(((role === 'PARTNER' || role === 'TEAM_MEMBER') || role === 'SUPPORT') && {
              field_officer_id: values.fieldOfficerId,
            }),

            ...((role === 'PARTNER' || role === 'TEAM_MEMBER') && {
              source: selectedPartner?.entity_name,
              admin_id: selectedPartner?.admin_id,
            }),

            ...(role === 'SUPPORT' && selectedPartner && {
              source: selectedPartner?.entity_name,
              admin_id: values.adminId,
            }),
          };

          dispatch(addFarmer(payload));
        }}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          errors,
          touched,
          resetForm,
        }) => {
          formApiRef.current = { setFieldValue };

          if (showSuccess) {
            return (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center max-w-2xl mx-auto mt-4">
                <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4 ring-6 ring-emerald-50/50">
                  <FaCheck className="w-7 h-7 text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-1.5">Registration Successful</h2>
                <p className="text-slate-500 mb-6 max-w-md mx-auto text-sm">
                  <span className="font-semibold text-slate-800">
                    {values.firstName} {values.lastName}
                  </span>{' '}
                  has been successfully added to the database.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate('/users')}
                    className="px-5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    View Profile
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSuccess(false);
                      dispatch(resetAddFarmerStatus());
                      resetForm({ values: initialValues });
                      setCountryCode('');
                      setStateCode('');
                      resetPincode();
                    }}
                    className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                  >
                    Register Another
                  </button>
                </div>
              </div>
            );
          }

          return (
            <form onSubmit={handleSubmit} className="divide-y divide-slate-100 max-w-6xl w-full mx-auto add-farmer-form">
              {addFarmerStatus === 'failed' && addFarmerError && (
                <div className="p-4 m-4 mb-0 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <FaExclamationCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-red-800">Registration Failed</h4>
                      <p className="text-xs text-red-600 mt-0.5">{addFarmerError}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => dispatch(resetAddFarmerStatus())}
                    className="text-xs text-red-700 hover:text-red-900 font-medium underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              {/* Section 1: Personal Info */}
              <div className="p-4 lg:p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-1.5 bg-indigo-50 rounded-lg text-indigo-600">
                    <FaUser className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">
                      Personal Information
                    </h3>
                    <p className="text-xs text-slate-500">
                      Basic identification details for the farmer.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pl-0 lg:pl-12">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-0.5">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <Field
                      name="firstName"
                      placeholder="ex. Ramesh"
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.firstName && touched.firstName
                        ? 'border-red-400'
                        : 'border-slate-300'
                        }`}
                    />
                    {errors.firstName && touched.firstName && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-0.5">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <Field
                      name="lastName"
                      placeholder="ex. Kumar"
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.lastName && touched.lastName
                        ? 'border-red-400'
                        : 'border-slate-300'
                        }`}
                    />
                    {errors.lastName && touched.lastName && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.lastName}</p>
                    )}
                  </div>
                  
                  {/* --- UPDATED MOBILE FIELD --- */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-0.5">
                      Mobile <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Field name="mobile">
                        {({ field, form }) => (
                          <PhoneInput
                            international
                            defaultCountry="IN"
                            placeholder="Enter Mobile *"
                            value={field.value}
                            onChange={(val) => {
                              form.setFieldValue(field.name, val);
                              form.setFieldTouched(field.name, true, false);
                            }}
                            onBlur={() => form.setFieldTouched(field.name, true)}
                            // Adapted Tailwind classes for the PhoneInput wrapper
                            className={`flex items-center w-full px-2.5 py-1.5 border rounded-lg text-sm bg-white focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 ${
                              errors.mobile && touched.mobile ? 'border-red-400' : 'border-slate-300'
                            }`}
                          />
                        )}
                      </Field>
                    </div>
                    {errors.mobile && touched.mobile && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.mobile}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-0.5">
                      Email (Optional)
                    </label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                        <FaEnvelope className="w-3.5 h-3.5" />
                      </span>
                      <Field
                        name="email"
                        type="email"
                        placeholder="farmer@example.com"
                        className="w-full pl-8 pr-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {((role === 'PARTNER' || role === 'TEAM_MEMBER') || role === 'SUPPORT') && (
                <div className="p-4 lg:p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-1.5 bg-blue-50 rounded-lg text-blue-600">
                      <FaUser className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Partner Mapping</h3>
                      <p className="text-xs text-slate-500">
                        Assign this farmer to a field officer under a partner.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 pl-0 lg:pl-12">
                    {role === 'SUPPORT' && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Partner <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={values.adminId}
                          onChange={(e) => {
                            const selectedAdminId = e.target.value;
                            setFieldValue('adminId', selectedAdminId);
                            setFieldTouched('adminId', true, false);
                            setFieldValue('fieldOfficerId', '');
                            setFieldTouched('fieldOfficerId', false, false);
                            if (selectedAdminId) {
                              dispatch(fetchFieldOfficersByAdminId({ admin_id: selectedAdminId }));
                            }
                          }}
                          className={`w-full px-3 py-2 border rounded-lg text-sm bg-white ${
                            values.adminId ? 'border-slate-300' : 'border-red-400'
                          }`}
                        >
                          <option value="">
                            {partnersStatus === 'loading' ? 'Loading partners...' : 'Select Partner'}
                          </option>
                          {partners.map((p) => (
                            <option key={p.admin_id} value={p.admin_id}>
                              {p.entity_name}
                            </option>
                          ))}
                        </select>
                        {errors.adminId && touched.adminId && (
                          <p className="text-red-500 text-xs mt-0.5">{errors.adminId}</p>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Field Officer
                      </label>
                      <select
                        value={values.fieldOfficerId}
                        onChange={(e) => {
                          setFieldValue('fieldOfficerId', e.target.value);
                          setFieldTouched('fieldOfficerId', true, false);
                        }}
                        disabled={
                          (role === 'SUPPORT' && !values.adminId) || fieldOfficersStatus === 'loading'
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm bg-white border-slate-300`}
                      >
                        <option value="">
                          {role === 'SUPPORT' && !values.adminId
                            ? 'Select Partner first'
                            : fieldOfficersStatus === 'loading'
                              ? 'Loading field officers...'
                              : 'Select Field Officer (Optional)'}
                        </option>
                        {fieldOfficersStatus === 'succeeded' &&
                          fieldOfficers.map((fo) => (
                            <option key={fo.field_officer_id} value={fo.field_officer_id}>
                              {fo.name}
                            </option>
                          ))}
                      </select>
                      {errors.fieldOfficerId && touched.fieldOfficerId && (
                        <p className="text-red-500 text-xs mt-0.5">{errors.fieldOfficerId}</p>
                      )}
                      {fieldOfficersStatus === 'succeeded' &&
                        (role !== 'SUPPORT' || values.adminId) &&
                        fieldOfficers.length === 0 && (
                          <p className="text-xs text-slate-500 mt-1">
                            No field officers found for this partner.
                          </p>
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Section 2: Location Details */}
              <div className="p-4 lg:p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-1.5 bg-emerald-50 rounded-lg text-emerald-600">
                    <FaMapMarkerAlt className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Location Details</h3>
                    <p className="text-xs text-slate-500">
                      Farm or residence address information.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 pl-0 lg:pl-12">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <Field
                      name="address"
                      placeholder="Street, locality, area"
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.address && touched.address
                        ? 'border-red-400'
                        : 'border-slate-300'
                        }`}
                    />
                    {errors.address && touched.address && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.address}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Zip / Postal Code <span className="text-red-500">*</span>
                    </label>
                    <Field
                      name="pincode"
                      placeholder="Enter Zip/Postal Code"
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.pincode && touched.pincode
                        ? 'border-red-400'
                        : 'border-slate-300'
                        }`}
                      onBlur={(e) => {
                        setFieldTouched('pincode', true);
                        const val = String(e.target.value).trim();
                        if (val.length === 6 && /^\d+$/.test(val) && (!countryCode || countryCode === 'IN')) {
                            lookupPincode(val);
                        }
                      }}
                      onChange={(e) => {
                        handleChange(e);
                        if (String(e.target.value).length !== 6) resetPincode();
                      }}
                    />
                    {pincodeLoading && (
                      <p className="text-slate-500 text-xs mt-0.5">Looking up address...</p>
                    )}
                    {errors.pincode && touched.pincode && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.pincode}</p>
                    )}
                  </div>

                  {pincodeData?.addressSuggestions?.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-0.5">
                        Suggested addresses (select to auto-fill)
                      </label>
                      <Select
                        isClearable
                        isSearchable
                        placeholder="Select address suggestion"
                        options={pincodeData.addressSuggestions}
                        onChange={(opt) => opt && setFieldValue('address', opt.value)}
                        className="text-sm"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: 38,
                            borderRadius: 8,
                          }),
                        }}
                      />
                    </div>
                  )}

                  {/* Country Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <Select
                      name="country"
                      options={updatedCountries()}
                      placeholder="Select Country"
                      value={values.country ? { label: values.country, value: countryCode } : null}
                      onChange={(opt) => {
                        setFieldValue('country', opt ? opt.label : '');
                        setCountryCode(opt ? opt.value : '');
                        
                        setFieldValue('state', '');
                        setStateCode('');
                        setFieldValue('city', '');
                        stateSelectRef.current?.clearValue();
                        cityRef.current?.clearValue();
                      }}
                      onBlur={() => setFieldTouched('country', true)}
                      className="text-sm"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: 38,
                          borderRadius: 8,
                          borderColor: errors.country && touched.country ? '#f87171' : '#cbd5e1',
                        }),
                      }}
                    />
                    {errors.country && touched.country && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.country}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      State <span className="text-red-500">*</span>
                    </label>
                    <Select
                      ref={stateSelectRef}
                      name="state"
                      options={updatedStates(countryCode)}
                      isDisabled={!countryCode}
                      placeholder="Select State"
                      value={values.state ? { label: values.state, value: stateCode } : null}
                      onChange={(opt) => {
                        setFieldValue('state', opt ? opt.label : '');
                        setStateCode(opt ? opt.value : '');
                        setFieldValue('city', '');
                        cityRef.current?.clearValue();
                      }}
                      onBlur={() => setFieldTouched('state', true)}
                      className="text-sm"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: 38,
                          borderRadius: 8,
                          borderColor: errors.state && touched.state ? '#f87171' : '#cbd5e1',
                        }),
                      }}
                    />
                    {errors.state && touched.state && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.state}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      City <span className="text-red-500">*</span>
                    </label>
                    <Select
                      ref={cityRef}
                      name="city"
                      options={updatedCities(countryCode, stateCode, pincodeData?.district)}
                      isDisabled={!stateCode}
                      placeholder="Select City"
                      value={values.city ? { label: values.city, value: values.city } : null}
                      onChange={(opt) => setFieldValue('city', opt ? opt.value : '')}
                      onBlur={() => setFieldTouched('city', true)}
                      className="text-sm"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: 38,
                          borderRadius: 8,
                          borderColor: errors.city && touched.city ? '#f87171' : '#cbd5e1',
                        }),
                      }}
                    />
                    {errors.city && touched.city && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.city}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 p-4 flex items-center justify-between border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    resetForm({ values: initialValues });
                    setCountryCode('');
                    setStateCode('');
                    resetPincode();
                    stateSelectRef.current?.clearValue();
                    cityRef.current?.clearValue();
                  }}
                  className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:text-slate-900 transition-colors"
                >
                  Clear Form
                </button>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={addFarmerStatus === 'loading'}
                    className={`
                        flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium text-white shadow-sm
                        transition-all duration-200
                        ${addFarmerStatus === 'loading'
                        ? 'bg-emerald-400 cursor-wait'
                        : 'bg-emerald-600 hover:bg-emerald-700 hover:shadow-emerald-600/20'
                      }
                      `}
                  >
                    {addFarmerStatus === 'loading' ? (
                      <>
                        <FaSpinner className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="w-4 h-4" />
                        Register Farmer
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </Wrapper>
  );
};

export default AddFarmer;